import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { getLocationOrThrow } from "@faire/web--source/common/globals/getLocation";
import { getWindowOrThrow } from "@faire/web--source/common/globals/getWindow";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web--source/common/localization/localize";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { optimizeImageUrl } from "@faire/web--source/common/transform-images";
import { Modal } from "@faire/web--source/slate/deprecated/Modal";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { LoadingSpinner } from "@faire/web--source/slate/LoadingSpinner";
import { Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { observer } from "mobx-react";
import React, { Suspense, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { LazyImageGallery } from "@faire/retailer/components/ImageGallery";
import { productMediaFromImages } from "@faire/retailer/components/ImageGallery/utils";
import { MessageBrandCTA } from "@faire/retailer/components/MessageBrandCTA/MessageBrandCTA";
import { showMessageBrandCTA } from "@faire/retailer/lib/messageBrandCTAUtils";
import { useMessengerFactory } from "@faire/retailer/stores/MessengerFactory";
var noop = function() {};
var SizeChartModalBase = function(param) {
    var brand = param.brand, product = param.product, onClose = param.onClose;
    var _useState = _sliced_to_array(useState(true), 2), isImageLoading = _useState[0], setIsImageLoading = _useState[1];
    var _useState1 = _sliced_to_array(useState(false), 2), showGallery = _useState1[0], setShowGallery = _useState1[1];
    var isMobile = useViewport().isMobile;
    var messengerStore = useMessengerFactory([
        "messengerStore"
    ]).messengerStore;
    useEffect(function() {
        messengerStore.setCurrentBrandContext(brand);
        return function() {
            messengerStore.setCurrentBrandContext(undefined);
        };
    }, [
        brand,
        messengerStore
    ]);
    var isSSR = useIsSSR();
    var imageUrl = useMemo(function() {
        var imgSrc = brand.brand_size_chart;
        /**
         * This is specifically to deal with the url being hardcoded in the database
         * for the size chart document. The document api requires an appropriate user
         * session and because this size chart is requested as an image and not an XHR,
         * we cannot intercept this request in our E2E tests. This block will only replace
         * the url hostname if it is localhost. This is less-than-ideal, but a solution
         * to have this feature tested in our E2E tests. This ensures that whatever
         * local forwarding proxy can apply whatever session tokens to the request.
         */ if ((imgSrc === null || imgSrc === void 0 ? void 0 : imgSrc.url) && getLocationOrThrow().hostname === "localhost") {
            var imgSrcUrl = new (getWindowOrThrow()).URL(imgSrc.url);
            // eslint-disable-next-line react-compiler/react-compiler
            imgSrc.url = getLocationOrThrow().origin + imgSrcUrl.pathname;
        }
        return imgSrc ? optimizeImageUrl(imgSrc, isSSR) : undefined;
    }, [
        brand.brand_size_chart,
        isSSR
    ]);
    useEffect(function() {
        // Preloading the size chart to prevent jumping around when it loads in
        // doing this because we are fitting the modal to the image
        if (isImageLoading) {
            var window = getWindowOrThrow();
            var image = new window.Image();
            image.onload = function() {
                return setIsImageLoading(false);
            };
            image.src = imageUrl;
        }
    }, [
        imageUrl,
        isImageLoading
    ]);
    if (isImageLoading) {
        return null;
    }
    if (showGallery && isMobile && brand.brand_size_chart) {
        return /*#__PURE__*/ _jsx(Suspense, {
            fallback: null,
            children: /*#__PURE__*/ _jsx(LazyImageGallery, {
                index: 0,
                productMedia: productMediaFromImages([
                    brand.brand_size_chart
                ]),
                onChangeIndex: noop,
                onClose: function() {
                    return setShowGallery(false);
                },
                eventName: "size-chart/".concat(brand === null || brand === void 0 ? void 0 : brand.token, "/").concat(product.token)
            })
        });
    }
    return /*#__PURE__*/ _jsx(ModalStyled, {
        isOpen: true,
        onClose: onClose,
        width: "auto",
        height: "auto",
        padding: "30px",
        "data-test-id": "size-chart-modal",
        children: /*#__PURE__*/ _jsxs(Container, {
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "sectionHeaderSansMedium",
                    children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "8GSEnI",
                        defaultMessage: [
                            {
                                type: 1,
                                value: "brandName"
                            },
                            {
                                type: 0,
                                value: " size chart"
                            }
                        ],
                        values: DEPRECATED_backwardsCompatibleValues({
                            brandName: brand === null || brand === void 0 ? void 0 : brand.name
                        })
                    })
                }),
                showMessageBrandCTA(brand) ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Spacer, {
                            size: 4
                        }),
                        /*#__PURE__*/ _jsxs(Row, {
                            children: [
                                /*#__PURE__*/ _jsx(Typography, {
                                    children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                        id: "ZRGnVV",
                                        defaultMessage: [
                                            {
                                                type: 0,
                                                value: "Questions about sizing?"
                                            }
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsx(Spacer, {
                                    size: 4
                                }),
                                /*#__PURE__*/ _jsx(MessageBrandCTA, {
                                    brand: brand
                                })
                            ]
                        })
                    ]
                }) : null,
                /*#__PURE__*/ _jsx(Spacer, {
                    size: 24
                }),
                /*#__PURE__*/ _jsx(StyledImage, {
                    src: imageUrl,
                    onClick: function() {
                        return setShowGallery(true);
                    },
                    "data-test-id": "size-chart-modal-image"
                }),
                isImageLoading ? /*#__PURE__*/ _jsx(ImagePlaceholder, {
                    children: /*#__PURE__*/ _jsx(LoadingSpinner, {
                        size: "large"
                    })
                }) : null
            ]
        })
    });
};
export var SizeChartModal = observer(SizeChartModalBase);
var ModalStyled = styled(Modal).withConfig({
    componentId: "sc-ecd74c2d-0"
})([
    "",
    "{min-height:600px;max-height:80vh;}"
], responsiveMediaQueries.tabletAndAbove);
var Container = styled(Column).withConfig({
    componentId: "sc-ecd74c2d-1"
})([
    "position:relative;",
    "{max-height:calc(80vh - 60px);}"
], responsiveMediaQueries.tabletAndAbove);
var StyledImage = styled.img.withConfig({
    componentId: "sc-ecd74c2d-2"
})([
    "width:100%;",
    "{min-width:500px;max-width:80vw;}"
], responsiveMediaQueries.tabletAndAbove);
var ImagePlaceholder = styled(Column).attrs({
    align: "center",
    justify: "center"
}).withConfig({
    componentId: "sc-ecd74c2d-3"
})([
    "height:calc(100vh - 132px);max-height:468px;"
]);
